import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { apiAdminshopsShopIdcategoriesPost } from '@r/api/generated/client.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Button } from '@ui/button.tsx';
import { invalidateAdminShopCategoriesQueries } from './custom-shop-categories-api.ts';
import {
  buildCustomShopCategoryFormSchema,
  type CustomShopCategoryFormValues
} from './custom-shop-category-form-schema.ts';

interface CreateCustomShopCategoryFormProps {
  shopId: number;
  onCancel: () => void;
  onSuccess: () => void;
}

/** Crée une catégorie produit rattachée à une boutique custom. */
export function CreateCustomShopCategoryForm({
  shopId,
  onCancel,
  onSuccess
}: CreateCustomShopCategoryFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formSchema = useMemo(() => buildCustomShopCategoryFormSchema(t), [t]);

  const form = useForm<CustomShopCategoryFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { label: '' },
    mode: 'onChange'
  });

  /** Crée la catégorie puis invalide le cache React Query du canvas. */
  const onSubmit = async (values: CustomShopCategoryFormValues) => {
    const normalizedLabel = values.label.trim();
    if (!normalizedLabel) return;

    setIsSubmitting(true);

    try {
      await apiAdminshopsShopIdcategoriesPost(String(shopId), { label: normalizedLabel });
      invalidateAdminShopCategoriesQueries(queryClient, shopId);

      toast.success(t('admin.shops.categories.create.success'));
      form.reset();
      onSuccess();
    } catch (error) {
      showOrvalErrorToast(error, t('admin.shops.categories.create.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form} schema={formSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="label"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.shops.categories.form.label.label')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('admin.shops.categories.form.label.placeholder')}
                  maxLength={100}
                  error={!!fieldState.error}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex gap-4 pt-4">
          <Button
            type="button"
            variant="outline"
            rounded="full"
            className="flex-1"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            <X className="size-4" />
            {t('label.cancel')}
          </Button>
          <Button type="submit" rounded="full" className="flex-1" loading={isSubmitting}>
            {t('admin.shops.categories.form.submit.create')}
            <Check className="size-4" />
          </Button>
        </div>
      </form>
    </Form>
  );
}
